/**
 * Client sends registry
 * ------------------------------------------------------------------
 * Maps a URL slug to a public-eligible deck ID. Each entry in this
 * file becomes a route at /c/<slug>.
 *
 * SECURITY MODEL:
 *  - Only deck IDs marked `audience: 'public'` in their meta.ts may
 *    appear here. The build verifies this at startup and refuses to
 *    boot a client build that references an internal deck.
 *  - Client builds (`VITE_BUILD_TARGET=client`) tree-shake every
 *    internal deck out of the bundle entirely. The code for those
 *    decks is not present in the JS the client downloads.
 *  - URL secrecy is the access control. Clients receive a unique
 *    link; treat it like a Notion share link or a Google Doc with
 *    link access. For stronger guarantees, layer Cloudflare Workers
 *    auth in front (see README).
 *
 * TO ADD A CLIENT SEND:
 *   1. Confirm the deck you want to send is `audience: 'public'`.
 *      If it isn't, that means a draft is leaking into a client URL —
 *      promote it to public deliberately, don't bypass.
 *   2. Add an entry below with a unique slug and the deck id.
 *   3. Deploy the client build. The new URL goes live at /c/<slug>.
 */

export interface ClientSend {
  /** URL-safe identifier — appears in /c/<slug>. */
  slug: string;
  /** The deck id (must reference a deck with audience: 'public'). */
  deckId: string;
  /** Display name for the recipient — appears in browser tab and footer. */
  client: string;
  /** ISO date the link was created — used in the footer for context. */
  sentAt: string;
  /** Optional: a brief note shown nowhere on the page; just for your records. */
  notes?: string;
}

export const clientSends: ClientSend[] = [
  // Example entry — uncomment and customize when you're ready to send.
  // {
  //   slug: 'lss-2026-05',
  //   deckId: 'v3-subtle',
  //   client: 'Legend Story Studios',
  //   sentAt: '2026-05-06',
  //   notes: 'Initial introductory meeting.',
  // },
];

/**
 * Look up a client send by slug. Returns undefined if the slug is unknown.
 */
export function findClientSend(slug: string): ClientSend | undefined {
  return clientSends.find((s) => s.slug === slug);
}
